/**
 * Calculates the points for a uniform distribution's Probability Density Function (PDF).
 * The PDF is 1 / (b - a) for x in [a, b], and 0 otherwise.
 * @param a - The minimum value of the distribution.
 * @param b - The maximum value of the distribution.
 * @param lbound - Lower boundary for data.
 * @param ubound - Upper boundary for data.
 * @returns An object containing x-values (labels) and y-values (data).
 */
export const uniformSample = (a: number, b: number, lbound?: number, ubound?: number) => {
  const labels: number[] = []
  const data: number[] = []

  // Calculate the height of the distribution (the PDF value)
  const pdfValue = 1 / (b - a)

  // Determine the range for the x-axis to properly visualize the function
  const range = b - a
  const xMin = a - range * 0.2 // Start before 'a'
  const xMax = b + range * 0.2 // End after 'b'

  // To create the sharp vertical lines at 'a' and 'b', we add points just before and at these values.
  // Point just before 'a' (value is 0)
  labels.push(xMin)
  data.push(0)

  // bound a / b values
  const abound = boundA(a, b, lbound, ubound)
  const bbound = boundB(b, abound, ubound)

  // Add a point very close to 'a' to make the line vertical
  labels.push(abound - 0.0001)
  data.push(0)

  // Point at 'a' (value is pdfValue)
  labels.push(abound)
  data.push(pdfValue)

  // Point at 'b' (value is pdfValue)
  labels.push(bbound)
  data.push(pdfValue)

  // Add a point very close after 'b' to make the line vertical
  labels.push(bbound + 0.0001)
  data.push(0)

  // Point after 'b' (value is 0)
  labels.push(xMax)
  data.push(0)

  // Sort points by label (x-value) to ensure Chart.js draws them correctly
  const sortedPoints: { label: number; value: number }[] = labels
    .map((label, index) => ({ label, value: data[index] ?? 0 }))
    .sort((p1, p2) => p1.label - p2.label)

  const sortedLabels = sortedPoints.map((p) => p.label)
  const sortedData = sortedPoints.map((p) => p.value)

  return { labels: sortedLabels, data: sortedData }
}

const boundA = (a: number, b: number, lbound?: number, ubound?: number) => {
  if (!lbound) return a
  if (lbound < a) return a
  if (ubound && lbound > ubound) return a
  if (lbound > b) return a
  return Math.max(a, lbound)
}

const boundB = (b: number, abound: number, ubound?: number) => {
  if (!ubound) return b
  if (ubound > b) return b
  if (ubound < abound) return b
  return Math.min(b, ubound)
}
